// ABOUTME: One listing as a card in the feed, in the PRO app's skin: who and what, the money as the backend derived
// ABOUTME: it with the qualifier that says which kind of number it is, the PJL badges, the hours, the places and the period.
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import { BoldChip } from 'components/BoldChip';
import { Text } from 'components/Text';
import { PATHS } from 'config/paths';
import { sendAtsJobListingOpened } from 'modules/analytics';
import { getTrans, getTransWithParams } from 'modules/i18n';
import { JobListingCard as JobListingCardData } from '../types';
import {
  contractPeriodLabel,
  formatMoney,
  hoursLabel,
  initialsOf,
  placesLabel,
  salaryQualifierKey,
  workplaceModeOf
} from '../utils';
import { ListingBadges } from './ListingBadges';

interface Props {
  card: JobListingCardData;
  /** Where the card sits in the list, for the analytics of what gets opened. */
  position: number;
}

/** A row of the card's facts: a label with a quieter line under it. */
const Fact = ({
  label,
  hint,
  testId,
  hintTestId
}: {
  label: string;
  hint?: string;
  testId?: string;
  hintTestId?: string;
}) => (
  <Box data-testid={testId}>
    <Text variant="body2" color="text.primary">
      {label}
    </Text>
    {hint && (
      <Text
        data-testid={hintTestId}
        variant="caption"
        color="text.secondary"
        component="p"
      >
        {hint}
      </Text>
    )}
  </Box>
);

export const JobListingCard = ({ card, position }: Props) => {
  // Where the channel hides the company, the occupation names the job; nothing here guesses the company.
  const who = card.companyName ?? card.occupationName;
  const hasApplied = !!card.myApplication;
  const mode = workplaceModeOf(card);
  const places = placesLabel(card);

  return (
    <Link
      to={`${PATHS.jobs}/${card.refId}`}
      style={{ display: 'block', textDecoration: 'none' }}
      onClick={() =>
        sendAtsJobListingOpened({
          jobListingRefId: card.refId,
          position,
          hasApplied
        })
      }
    >
      <Card
        data-testid="job-listing-card"
        sx={{ borderRadius: 4, mb: 2, boxShadow: 'none' }}
      >
        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 1.5
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Text
                data-testid="job-listing-company"
                variant="subtitle1"
                component="h3"
                color="text.primary"
                sx={{ fontWeight: 700, lineHeight: 1.3 }}
              >
                {who}
              </Text>
              <Text variant="body1" color="text.primary" sx={{ mt: 0.5 }}>
                {card.title}
              </Text>
            </Box>
            <Box
              aria-hidden="true"
              sx={{
                flexShrink: 0,
                width: 40,
                height: 40,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                backgroundColor: 'action.hover',
                fontSize: 12,
                fontWeight: 700,
                color: 'primary.main'
              }}
            >
              {initialsOf(who)}
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              mt: 2
            }}
          >
            <Box>
              <Text variant="caption" color="text.secondary" component="p">
                {getTrans(salaryQualifierKey(card))}
              </Text>
              <Text
                data-testid="job-listing-monthly"
                variant="h6"
                component="p"
                color="text.primary"
                sx={{ fontWeight: 800, lineHeight: 1.2 }}
              >
                {getTransWithParams('jobs.card.perMonth', {
                  amount: formatMoney(card.monthlySalaryMax)
                })}
              </Text>
              <Text
                data-testid="job-listing-hourly"
                variant="caption"
                color="text.secondary"
                component="p"
              >
                {getTransWithParams('jobs.card.perHour', {
                  amount: formatMoney(card.hourlyRate)
                })}
              </Text>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {hasApplied && (
                <BoldChip
                  data-testid="job-listing-applied"
                  variant="soft"
                  size="small"
                  color="success"
                  label={getTrans('jobs.card.applied')}
                />
              )}
              <BoldChip
                variant="soft"
                size="small"
                color="primary"
                label={card.occupationName.toUpperCase()}
              />
            </Box>
          </Box>

          <Box sx={{ mt: 1.5 }}>
            <ListingBadges card={card} />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'grid', gap: 1.5 }}>
            <Fact
              testId="job-listing-hours"
              label={getTransWithParams('jobs.card.hoursPerWeek', {
                hours: hoursLabel(
                  card.minWeeklyHourMinutes,
                  card.maxWeeklyHourMinutes
                )
              })}
            />
            {mode === 'REMOTE' ? (
              <Fact label={getTrans('jobs.card.remote')} />
            ) : (
              places && (
                <Fact
                  testId="job-listing-place"
                  label={
                    mode === 'HYBRID'
                      ? `${getTrans('jobs.card.hybrid')} · ${places.label}`
                      : places.label
                  }
                  hint={places.hint}
                  hintTestId="job-listing-distance"
                />
              )
            )}
            <Fact
              testId="job-listing-period"
              label={contractPeriodLabel(card)}
              hint={getTrans(
                card.endDate || card.unlimitedContract
                  ? 'jobs.card.contract'
                  : 'jobs.card.startDate'
              )}
            />
          </Box>
        </CardContent>
      </Card>
    </Link>
  );
};
